import { formatCentsCompact, formatHoldPeriodMonths } from './marketplaceFormatters'

export interface FieldChange {
  field: string
  label: string
  from: string
  to: string
  longText: boolean
}

type DistributionFrequency = 'monthly' | 'quarterly' | 'annually'

const NOISE_FIELDS = new Set([
  'id',
  'created_at',
  'createdAt',
  'updated_at',
  'updatedAt',
  'version_number',
  'versionNumber',
  'deleted_at',
  'deletedAt',
  'listed_by',
  'listedBy',
  'publishedAt',
  'published_at',
])

const FIELD_LABELS: Record<string, string> = {
  title: 'Title',
  description: 'Description',
  propertyType: 'Property Type',
  addressStreet: 'Street',
  addressCity: 'City',
  addressState: 'State',
  addressZip: 'ZIP',
  askingPriceCents: 'Asking Price',
  capRate: 'Cap Rate',
  noi: 'NOI',
  images: 'Images',
  published: 'Status',
  dst: 'DST',
  sponsor: 'Sponsor',
  dstMinInvestmentCents: 'Min Investment',
  dstExpectedReturnMin: 'Expected Return Min',
  dstExpectedReturnMax: 'Expected Return Max',
  dstHoldPeriodMonths: 'Hold Period',
  dstDistributionFrequency: 'Distribution Frequency',
  dstAssetClass: 'Asset Class',
  dstOfferingSizeCents: 'Offering Size',
  qualifiedStatus: 'Qualified Status',
  orgId: 'Org',
}

const CENTS_FIELDS = new Set([
  'askingPriceCents',
  'dstMinInvestmentCents',
  'dstOfferingSizeCents',
])

const PERCENT_FIELDS = new Set([
  'capRate',
  'dstExpectedReturnMin',
  'dstExpectedReturnMax',
])

const LONG_TEXT_THRESHOLD = 60

function formatDistributionFrequency(value: unknown): string {
  if (value == null || value === '') return '—'
  const map: Record<DistributionFrequency, string> = {
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    annually: 'Annually',
  }
  return map[value as DistributionFrequency] ?? String(value)
}

function formatQualifiedStatus(value: unknown): string {
  if (value == null || value === '') return '—'
  const map: Record<string, string> = {
    qualified: 'Qualified',
    pending_review: 'Pending Review',
    unqualified: 'Unqualified',
  }
  return map[String(value)] ?? String(value)
}

function formatBoolean(value: unknown): string {
  if (value == null) return '—'
  return value ? 'Yes' : 'No'
}

function formatPublished(value: unknown): string {
  if (value == null) return '—'
  return value ? 'Published' : 'Draft'
}

function formatImageArray(value: unknown): string {
  if (!Array.isArray(value)) return '—'
  return `${value.length} image${value.length !== 1 ? 's' : ''}`
}

function formatFieldValue(field: string, value: unknown): string {
  if (value == null || value === '') return '—'
  if (field === 'images') return formatImageArray(value)
  if (field === 'published') return formatPublished(value)
  if (field === 'dst') return formatBoolean(value)
  if (field === 'dstDistributionFrequency') return formatDistributionFrequency(value)
  if (field === 'qualifiedStatus') return formatQualifiedStatus(value)
  if (CENTS_FIELDS.has(field)) return formatCentsCompact(Number(value))
  if (PERCENT_FIELDS.has(field)) return `${value}%`
  if (field === 'dstHoldPeriodMonths') return formatHoldPeriodMonths(Number(value))
  return String(value)
}

function formatImageDiff(fromValue: unknown, toValue: unknown): { from: string; to: string } {
  const fromArray = Array.isArray(fromValue) ? (fromValue as string[]) : []
  const toArray = Array.isArray(toValue) ? (toValue as string[]) : []
  const fromSet = new Set(fromArray)
  const toSet = new Set(toArray)
  const added = toArray.filter((url) => !fromSet.has(url)).length
  const removed = fromArray.filter((url) => !toSet.has(url)).length
  const parts: string[] = []
  if (added > 0) parts.push(`Added ${added}`)
  if (removed > 0) parts.push(`Removed ${removed}`)
  const summary = parts.length > 0 ? parts.join(', ') : 'Reordered'
  return { from: formatImageArray(fromValue), to: `${formatImageArray(toValue)} (${summary})` }
}

export function computePropertyDiff(from: object, to: object): FieldChange[] {
  const fromRecord = from as Record<string, unknown>
  const toRecord = to as Record<string, unknown>
  const allFields = new Set([...Object.keys(fromRecord), ...Object.keys(toRecord)])
  const changes: FieldChange[] = []
  for (const field of allFields) {
    if (NOISE_FIELDS.has(field)) continue
    const fromValue = fromRecord[field]
    const toValue = toRecord[field]
    if (JSON.stringify(fromValue) === JSON.stringify(toValue)) continue
    const label = FIELD_LABELS[field] ?? field
    let fromFormatted: string
    let toFormatted: string
    if (field === 'images') {
      const imageDiff = formatImageDiff(fromValue, toValue)
      fromFormatted = imageDiff.from
      toFormatted = imageDiff.to
    } else {
      fromFormatted = formatFieldValue(field, fromValue)
      toFormatted = formatFieldValue(field, toValue)
    }
    const longText = fromFormatted.length > LONG_TEXT_THRESHOLD || toFormatted.length > LONG_TEXT_THRESHOLD
    changes.push({ field, label, from: fromFormatted, to: toFormatted, longText, })
  }
  return changes
}
